var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect } from 'react';
import Truncate from 'react-truncate-markup';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { FollowRequestStatus } from '@amityco/js-sdk';
import ConditionalRender from '../../../core/components/ConditionalRender';
import Button from '../../../core/components/Button';
import customizableComponent from '../../../core/hocs/customization';
import { backgroundImage as UserImage } from '../../../icons/User';
import { canPerformUserLookups } from '../../../helpers/permissions';
import { getUserType } from '../../../helpers/userTypes';
import { useConfig } from '../../../social/providers/ConfigProvider';
import { PENDING_TAB } from '../../../social/pages/UserFeed/Followers/constants';
import BanIcon from '../../../icons/Ban';
import { UserMetadata } from './UserMetadata';
import { Avatar, BigBee, Container, ProfileName, Header, Description, PencilIcon, OptionMenu, PendingNotification, NotificationTitle, NotificationBody, TitleEllipse, ActionButtonContainer, ProfileNameWrapper, } from './styles';
import { UserFeedTabs } from '../../../social/pages/UserFeed/constants';
import { confirm } from '../../../core/components/Confirm';
import useUser from '../../../core/hooks/useUser';
import useReport from '../../../social/hooks/useReport';
import { useAsyncCallback } from '../../../core/hooks/useAsyncCallback';
import { notification } from '../../../core/components/Notification';
import useFollowersList from '../../../core/hooks/useFollowersList';
import { useSDK } from '../../../core/hooks/useSDK';
const UIUserInfo = ({ userId, currentUserId, fileUrl, displayName, description, isMyProfile, onEditUser, onFollowDecline, isFollowAccepted, setActiveTab, setFollowActiveTab, isPrivateNetwork, showUserProfileMetadata, }) => {
    const { user: currentUser } = useUser(currentUserId);
    const { user } = useUser(userId);
    const { isFlaggedByMe, handleReport } = useReport(user);
    const { formatMessage } = useIntl();
    const { connected } = useSDK();
    const [onReportClick] = useAsyncCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        yield handleReport();
        notification.success({
            content: (React.createElement(FormattedMessage, { id: isFlaggedByMe ? 'report.unreportSent' : 'report.reportSent' })),
        });
    }), [handleReport]);
    const title = user.displayName
        ? formatMessage({ id: 'user.unfollow.confirm.title' }, { displayName: user.displayName })
        : formatMessage({ id: 'user.unfollow.confirm.title.thisUser' });
    const content = user.displayName
        ? formatMessage({ id: 'user.unfollow.confirm.body' }, { displayName: user.displayName })
        : formatMessage({ id: 'user.unfollow.confirm.body.thisUser' });
    const currentUserType = getUserType(currentUser);
    const profileUserType = getUserType(user);
    const [userInStingDash, setUserInStingDash] = useState(false);
    const { isUserCurrentlyInStingDashCallback, manateeUrl, dashboardUrl } = useConfig();
    useEffect(() => {
        if (currentUserType === "coach") {
            (() => __awaiter(void 0, void 0, void 0, function* () {
                const inStingDash = yield isUserCurrentlyInStingDashCallback(userId);
                setUserInStingDash(inStingDash);
            }))();
        }
    }, [userId, currentUserType]);
    const onLookupUser = () => {
        window.open(`${manateeUrl}/user/${userId}`, '_blank');
    };
    const onOpenUserDashboard = () => {
        window.open(`${dashboardUrl}/inbox/${currentUserId}/coachee/${userId}`, '_blank');
    };
    const allOptions = [
        isFollowAccepted &&
            !isMyProfile && {
            name: 'user.unfollow',
            action: () => confirm({
                title,
                content,
                cancelText: formatMessage({ id: 'buttonText.cancel' }),
                okText: formatMessage({ id: 'buttonText.unfollow' }),
                onOk: () => __awaiter(void 0, void 0, void 0, function* () {
                    yield onFollowDecline();
                    setActiveTab(UserFeedTabs.TIMELINE);
                }),
            }),
        },
        !isMyProfile && {
            name: isFlaggedByMe ? 'report.unreportUser' : 'report.reportUser',
            action: onReportClick,
        },
        canPerformUserLookups({
            actingUserType: currentUserType,
            targetUserType: profileUserType,
        }) && {
            name: 'post.manatee',
            action: onLookupUser,
        },
        canPerformUserLookups({
            actingUserType: currentUserType,
            targetUserType: profileUserType,
        }) && {
            name: 'post.dashboard',
            action: onOpenUserDashboard,
        },
    ].filter(Boolean);
    const [pendingUsers] = useFollowersList(currentUserId, FollowRequestStatus.Pending);
    return (React.createElement(React.Fragment, null,
        React.createElement(Container, { "data-qa-anchor": "user-info" },
            React.createElement(Header, null,
                React.createElement(Avatar, { size: "big", displayName: user.displayName, "data-qa-anchor": "user-info-profile-image", avatar: fileUrl, backgroundImage: UserImage }),
                userInStingDash && React.createElement(BigBee, null, "\uD83D\uDC1D"),
                React.createElement(ActionButtonContainer, null,
                    React.createElement(ConditionalRender, { condition: isMyProfile },
                        React.createElement(Button, { "data-qa-anchor": "user-info-edit-profile-button", disabled: !connected, onClick: () => onEditUser(userId) },
                            React.createElement(PencilIcon, null),
                            " ",
                            React.createElement(FormattedMessage, { id: "user.editProfile" })))),
                React.createElement(OptionMenu, { options: allOptions, pullRight: false })),
            React.createElement(ProfileNameWrapper, null,
                React.createElement(Truncate, { lines: 3 },
                    React.createElement(ProfileName, { "data-qa-anchor": "user-info-profile-name" }, displayName)),
                user.isGlobalBan && (React.createElement(BanIcon, { width: 14, height: 14, css: "margin-left: 0.265rem; margin-top: 1px;" }))),
            React.createElement(Description, { "data-qa-anchor": "user-info-description" }, description),
            isMyProfile && pendingUsers.length > 0 && isPrivateNetwork && (React.createElement(PendingNotification, { onClick: () => {
                    setActiveTab(UserFeedTabs.FOLLOWERS);
                    setTimeout(() => setFollowActiveTab(PENDING_TAB), 250);
                } },
                React.createElement(NotificationTitle, null,
                    React.createElement(TitleEllipse, null),
                    React.createElement(FormattedMessage, { id: "follow.pendingNotification.title" })),
                React.createElement(NotificationBody, null,
                    React.createElement(FormattedMessage, { id: "follow.pendingNotification.body" }))))),
        showUserProfileMetadata && user.userId && (React.createElement(Container, { "data-qa-anchor": "user-metadata" },
            React.createElement(UserMetadata, { user: user })))));
};
UIUserInfo.propTypes = {
    userId: PropTypes.string,
    currentUserId: PropTypes.string,
    fileUrl: PropTypes.string,
    displayName: PropTypes.string,
    description: PropTypes.string,
    isMyProfile: PropTypes.bool,
    isFollowPending: PropTypes.bool,
    isFollowNone: PropTypes.bool,
    isFollowAccepted: PropTypes.bool,
    setActiveTab: PropTypes.func,
    setFollowActiveTab: PropTypes.func,
    followerCount: PropTypes.number,
    followingCount: PropTypes.number,
    isPrivateNetwork: PropTypes.bool,
    onEditUser: PropTypes.func,
    onFollowRequest: PropTypes.func,
    onFollowDecline: PropTypes.func,
};
UIUserInfo.defaultProps = {
    userId: '',
    currentUserId: '',
    fileUrl: '',
    displayName: '',
    description: '',
    isMyProfile: false,
    onEditUser: () => { },
    onFollowRequest: () => null,
    onFollowDecline: () => null,
    isFollowPending: false,
    isFollowNone: false,
    isFollowAccepted: false,
    setActiveTab: () => null,
    setFollowActiveTab: () => null,
    followerCount: 0,
    followingCount: 0,
};
export default customizableComponent('UIUserInfo', UIUserInfo);
//# sourceMappingURL=UIUserInfo.js.map